import type { GamificationActionDetail, GamificationConfig } from '@chat-emp/shared';

import { isFirstReplyPending, type TailMessage } from '../data/first-reply.js';

/**
 * The host's coin budget, and the gate on reporting a reward.
 *
 * Module state rather than React state because the send path (`data/source.ts`)
 * is not React — the same reason `setReauthTrigger` exists. `SessionProvider` is
 * the only writer: it keeps its own copy for rendering and mirrors it here.
 */
let config: GamificationConfig | null = null;

export function setGamificationConfig(next: GamificationConfig | null): void {
  config = next;
}

export function getGamificationConfig(): GamificationConfig | null {
  return config;
}

/**
 * The session's company, which every reward is reported against (AI-3134).
 * Never the Stream user id: after the cutover that names the recruiter. Empty
 * means an older server, and then nothing is reported rather than a guess.
 */
let companyId = '';

export function setGamificationCompanyId(next: string): void {
  companyId = next;
}

export interface FirstReplyEmitArgs {
  /** The tail EXCLUDING the message just sent — see below. */
  priorMessages: TailMessage[];
  currentUserId: string | undefined;
  channelId: string;
  jobseekerId: string | undefined;
  jobId: string | undefined;
}

/**
 * The `gamification-action` detail for an employer send, or null when the send
 * does not qualify.
 *
 * Judged against the state *before* the send: afterwards the first reply is no
 * longer pending, so a second send can never re-qualify. That is what makes the
 * reward fire exactly once per channel with no bookkeeping.
 */
export function firstReplyEmitDetail(args: FirstReplyEmitArgs): GamificationActionDetail | null {
  const { priorMessages, currentUserId, channelId, jobseekerId, jobId } = args;
  if (!currentUserId || !channelId || !companyId) return null;
  // Job-bounded chats only: a direct chat has no application to reward.
  if (!jobId) return null;

  const coins = config?.coins.first_reply ?? 0;
  const limit = config?.coins.limit ?? 0;
  if (coins <= 0 || limit <= 0) return null;

  if (!isFirstReplyPending(priorMessages, currentUserId)) return null;

  return {
    channelId,
    companyId,
    ...(jobseekerId ? { jobseekerId } : {}),
    jobId,
    type: 'first_reply',
    coins,
  };
}

export interface InitChatEmitArgs {
  channelId: string;
  jobseekerId: string | undefined;
  jobId: string | undefined;
}

/**
 * The `gamification-action` detail for starting a conversation, or null when the
 * start does not qualify.
 *
 * Job-bounded chats only, exactly as `firstReplyEmitDetail` is: a direct chat has
 * no application to reward. That keeps one rule across both actions rather than
 * two that agree until someone changes one of them.
 *
 * Reported only once the channel actually exists — this is called after the send
 * that creates it, so an abandoned compose earns nothing, which is the whole
 * point of creating on send.
 */
export function initChatEmitDetail(args: InitChatEmitArgs): GamificationActionDetail | null {
  const { channelId, jobseekerId, jobId } = args;
  if (!channelId || !companyId) return null;
  if (!jobId) return null;

  const coins = config?.coins.init_chat ?? 0;
  const limit = config?.coins.limit ?? 0;
  // No budget, no nudge and no report — chat-emp never invents an amount.
  if (coins <= 0 || limit <= 0) return null;

  return {
    channelId,
    companyId,
    ...(jobseekerId ? { jobseekerId } : {}),
    jobId,
    type: 'init_chat',
    coins,
  };
}
